// ============================================================================
// AuthModal — Login / Register modal + PaywallBanner
// ============================================================================
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogTitle, Box, Typography, TextField,
  Button, Divider, Alert, IconButton, Tabs, Tab, Stack, Chip, alpha, useTheme,
} from '@mui/material';
import { Close, Lock, AutoAwesome, CheckCircle, ArrowBack } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/apiService';

// ── Inline tab panel ──────────────────────────────────────────────────────
function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// ── Forgot password view ──────────────────────────────────────────────────
function ForgotPasswordView({ onBack }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.forgotPassword(email);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <Box sx={{ pt: 2 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Check your inbox — we sent a password reset link to <strong>{email}</strong>. It expires in 1 hour.
        </Alert>
        <Button variant="text" size="small" startIcon={<ArrowBack />} onClick={onBack}>
          Back to sign in
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter your email and we'll send you a link to reset your password.
      </Typography>
      <form onSubmit={submit}>
        <Stack spacing={2}>
          {error && <Alert severity="error" sx={{ py: 0.5, fontSize: '0.82rem' }}>{error}</Alert>}
          <TextField
            label="Email" type="email" size="small" fullWidth required
            value={email} onChange={e => setEmail(e.target.value)} autoComplete="email"
          />
          <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ fontWeight: 700, borderRadius: 2 }}>
            {loading ? 'Sending…' : 'Send reset link'}
          </Button>
        </Stack>
      </form>
      <Button variant="text" size="small" startIcon={<ArrowBack />} onClick={onBack} sx={{ mt: 1.5 }}>
        Back to sign in
      </Button>
    </Box>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────
export function AuthModal({ open, onClose, defaultTab = 0 }) {
  const { login, register } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const [view, setView] = useState('auth'); // 'auth' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => { setEmail(''); setPassword(''); setError(''); setLoading(false); setView('auth'); };

  const handleClose = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (tab === 0) {
        await login(email, password);
      } else {
        await register(email, password);
      }
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {view === 'forgot' ? 'Reset password' : 'The Cycle'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}><Close fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        {view === 'forgot' ? (
          <ForgotPasswordView onBack={() => setView('auth')} />
        ) : (
          <>
            <Tabs value={tab} onChange={(_, v) => { setTab(v); setError(''); }} sx={{ mb: 0.5 }}>
              <Tab label="Sign in" />
              <Tab label="Create account" />
            </Tabs>

            <TabPanel value={tab} index={1}>
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: alpha('#1976d2', 0.07), border: '1px solid', borderColor: alpha('#1976d2', 0.18) }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Free account includes:</Typography>
                <Stack spacing={0.5}>
                  {['5 AI queries per day', 'Full leaderboards + player stats', 'CVR/ACVR analytics'].map(f => (
                    <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                      <Typography variant="caption">{f}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </TabPanel>

            <form onSubmit={submit}>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {error && <Alert severity="error" sx={{ py: 0.5, fontSize: '0.82rem' }}>{error}</Alert>}
                <TextField
                  label="Email" type="email" size="small" fullWidth required
                  value={email} onChange={e => setEmail(e.target.value)} autoComplete="email"
                />
                <TextField
                  label="Password" type="password" size="small" fullWidth required
                  value={password} onChange={e => setPassword(e.target.value)}
                  autoComplete={tab === 0 ? 'current-password' : 'new-password'}
                  helperText={tab === 1 ? 'At least 8 characters' : undefined}
                />
                <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ fontWeight: 700, borderRadius: 2 }}>
                  {loading ? 'Please wait…' : tab === 0 ? 'Sign in' : 'Create free account'}
                </Button>
              </Stack>
            </form>

            {tab === 0 && (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  No account?{' '}
                  <Box component="span" sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600 }} onClick={() => setTab(1)}>
                    Sign up free
                  </Box>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500 }}
                  onClick={() => setView('forgot')}
                >
                  Forgot password?
                </Typography>
              </Stack>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Paywall banner — shown when query limit is hit ────────────────────────
export function PaywallBanner({ tier, limit, onSignUp, onUpgrade, onDismiss }) {
  const theme = useTheme();
  const isAnon = tier === 'anonymous';

  return (
    <Box sx={{
      maxWidth: 680, mx: 'auto', mt: 2,
      p: 3, borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
      background: alpha(theme.palette.warning.main, 0.05),
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Lock sx={{ color: 'warning.main', mt: 0.25, flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            {isAnon ? "You've used today's free queries" : "Daily query limit reached"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isAnon
              ? `Anonymous users get ${limit} AI queries/day. Create a free account for 5/day, or go Pro for unlimited access to the AI search engine.`
              : `Free accounts get ${limit} AI queries/day. Upgrade to Pro for unlimited queries, no throttling.`}
          </Typography>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
            {isAnon && (
              <Button variant="contained" size="small" onClick={onSignUp} sx={{ fontWeight: 700, borderRadius: 2 }}>
                Create free account
              </Button>
            )}
            <Button
              variant={isAnon ? 'outlined' : 'contained'}
              size="small"
              onClick={onUpgrade}
              sx={{ fontWeight: 700, borderRadius: 2 }}
              startIcon={<AutoAwesome sx={{ fontSize: '14px !important' }} />}
            >
              Go Pro — $8/mo
            </Button>
            {!isAnon && (
              <Button variant="text" size="small" color="inherit" onClick={onDismiss}>
                Maybe later
              </Button>
            )}
          </Stack>

          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {['Unlimited AI queries', 'CVR Movers feed', 'Saved leaderboards (coming soon)'].map(f => (
              <Chip key={f} label={f} size="small" icon={<CheckCircle sx={{ fontSize: '12px !important' }} />}
                sx={{ fontSize: '0.72rem', height: 22, bgcolor: alpha(theme.palette.success.main, 0.08), color: 'success.main', borderColor: alpha(theme.palette.success.main, 0.25) }} variant="outlined" />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
